import { useEffect, RefObject } from 'react';

/**
 * Aarthika Pro-style keyboard navigation hook.
 * - Enter moves to next focusable field (like Tab)
 * - On SELECT elements: Enter closes dropdown and moves to next field (Aarthika style)
 * - On last field: Enter triggers save (looks for data-save-btn or submit button)
 * - Escape closes/quits the form (triggers data-quit-btn click)
 * - Ctrl+A or End = Accept/Save from anywhere
 */
export const useEnterAsTab = (containerRef: RefObject<HTMLElement>) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const container = containerRef.current;
      if (!container) return;

      // ── Ctrl+A or End → trigger save ──────────────────────────────────────
      if ((e.ctrlKey && e.key === 'a') || e.key === 'End') {
        e.preventDefault();
        e.stopPropagation();
        const saveBtn = container.querySelector<HTMLElement>('[data-save-btn]');
        if (saveBtn) { saveBtn.click(); return; }
        const submitBtn = container.querySelector<HTMLButtonElement>('button[type="submit"]');
        if (submitBtn) { submitBtn.click(); return; }
        return;
      }

      // ── Escape → quit/close ────────────────────────────────────────────────
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        const quitBtn = container.querySelector<HTMLElement>('[data-quit-btn]');
        if (quitBtn) { quitBtn.click(); return; }
        return;
      }

      // ── Enter → Tab-like navigation ────────────────────────────────────────
      if (e.key === 'Enter') {
        // Textarea: Enter inserts newline (don't intercept)
        if (target.tagName === 'TEXTAREA') return;

        const focusableSelector =
          'input:not([disabled]):not([readonly]):not([type="hidden"]), ' +
          'select:not([disabled]), ' +
          'textarea:not([disabled]), ' +
          '[tabindex]:not([tabindex="-1"])';

        const allElements = (Array.from(
          container.querySelectorAll<HTMLElement>(focusableSelector)
        ) as HTMLElement[]).filter((el: HTMLElement) => {
          // Skip hidden/invisible elements
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
        });

        const index = allElements.indexOf(target);
        if (index === -1) return;

        e.stopPropagation();

        // Last field → trigger save
        if (index === allElements.length - 1) {
          e.preventDefault();
          const saveBtn = container.querySelector<HTMLElement>('[data-save-btn]');
          if (saveBtn) { saveBtn.click(); return; }
          const submitBtn = container.querySelector<HTMLButtonElement>('button[type="submit"]');
          if (submitBtn) { submitBtn.click(); }
          return;
        }

        // Move to next focusable field
        e.preventDefault();
        const next = allElements[index + 1];
        next.focus();

        // If next element is a number input, select all text for easy overwrite
        if (next.tagName === 'INPUT' && (next as HTMLInputElement).type === 'number') {
          (next as HTMLInputElement).select();
        }
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [containerRef]);
};
